'use client';
import React, { useState } from 'react';
import { ReactComponent as SpecSheetIcon } from '../../../static/product_image/specsheetIcon.svg';

import { ReactComponent as DataSheetIcon } from '../../../static/product_image/datasheetIcon.svg';

import { ReactComponent as EmaIcon } from '../../../static/product_image/EMA Icon.svg';
import { ReactComponent as YsimIcon } from '../../../static/product_image/Y-sim.svg';
import { ReactComponent as SpiceIcon } from '../../../static/product_image/spice.svg';
import { ReactComponent as RohsIcon } from '../../../static/product_image/ROHS.svg';
import { ReactComponent as StepCadIcon } from '../../../static/product_image/Step-Cad Icon.svg';
import { ReactComponent as InfoButton } from '../../../static/product_image/Vector.svg';
import { Box } from '@mui/material';
import { PIMCORE_CONTENT_BASE_URL } from '../../../utils/Pimcore';
import { Tooltip } from 'antd';
import { CS_BASE_URL, linkPrefix, NEW_CS_BASE_URL } from '../../../utils/ProjectUrls';
import { ReturnParameterValue } from '../../../schemas/ReturnParameterValue';
import { getParameterFormattedValueByParameterId } from '../../../utils/ParameterRelatedFunction';
import { ProductsContent } from '../../../graphql/pimcore/modules/siteContent/types/ProductsContent';
import { callLoadPartDiv } from '@/utils/kemetPlugin';

interface IconContainerProps {
    dataSheet: string;
    partNumber: string;
    rohs: boolean;
    ksimSupport: boolean;
    spiceFile: boolean;
    step: string;
    manufacturer: string;
    specsheetSuppression: boolean;
    unQualifyInfo?: string | null | undefined;
    parameterValues: ReturnParameterValue[];
    contentTree: ProductsContent;
}

const IconContainer: React.FC<IconContainerProps> = ({
    dataSheet,
    partNumber,
    rohs,
    ksimSupport,
    spiceFile,
    step,
    manufacturer,
    specsheetSuppression,
    unQualifyInfo,
    parameterValues,
    contentTree,
}) => {
    // Tooltip 是否可見
    const [visible, setVisible] = useState(false);

    // 當 Tooltip 顯示 / 隱藏時
    const handleVisibleChange = (nextVisible: boolean) => {
        setVisible(nextVisible);
    };

    const handleIconClick = (url: string, newTab: boolean = true) => {
        if (newTab) {
            window.open(url, '_blank');
        } else {
            window.location.href = url;
        }
    };

    const iconStyle = {
        display: 'inline-flex',
        marginRight: '15px',
        cursor: 'pointer',
        '&:hover path': {
            fill: '#E5673E',
        },
    };

    const handleEDAMCADClick = (event: React.MouseEvent) => {
        event.preventDefault();
        const ok = callLoadPartDiv({ manufacturer, partNumber, language: 'en-US' });
        console.log(
            '[EDA/MCAD] callLoadPartDiv returned:',
            ok,
            'typeof window.loadPartDiv =',
            typeof window !== 'undefined' && typeof (window as any).loadPartDiv
        );

        if (!ok) {
            // 這代表：不是在瀏覽器、或 plugin 還沒載好
            // 先提示你自己（之後可以改成 UI 提示）
            alert('EDA/MCAD 插件尚未就緒（loadPartDiv 不存在）');
        }
    };

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'left',
                marginTop: '1px',
                width: '100%',
            }}
        >
            {/*if specsheetSuppression === true, do not display*/}
            {!specsheetSuppression && (
                <Box sx={iconStyle}>
                    <SpecSheetIcon
                        title="Specsheet"
                        onClick={() =>
                            handleIconClick(
                                `${NEW_CS_BASE_URL}/component-documentation/download/specsheet/${partNumber}`,
                                true
                            )
                        }
                    />
                </Box>
            )}
            {dataSheet && (
                <Box sx={iconStyle}>
                    <DataSheetIcon
                        title="Datasheet"
                        onClick={() =>
                            window.open(
                                PIMCORE_CONTENT_BASE_URL +
                                    `/datasheet/asset/file/${encodeURIComponent(dataSheet)}`,
                                '_blank'
                            )
                        }
                    />
                </Box>
            )}
            {ksimSupport && (
                <Box sx={iconStyle}>
                    <YsimIcon
                        title="Y-SIM"
                        onClick={() =>
                            handleIconClick(
                                `https://ksim3.kemet.com/capacitor-simulation?pn=${partNumber}`,
                                true
                            )
                        }
                    />
                </Box>
            )}

            {getParameterFormattedValueByParameterId(parameterValues, 364) === 'Yes' && (
                <Box
                    sx={iconStyle}
                    onClick={() =>
                        handleIconClick(
                            `https://elc.kemet.com/?part=${encodeURIComponent(partNumber)}`
                        )
                    }
                >
                    <YsimIcon title="Y-SIM" />
                    Y-SIM
                </Box>
            )}

            {spiceFile && (
                <Box sx={iconStyle}>
                    <SpiceIcon
                        title="Spice File"
                        onClick={() =>
                            handleIconClick(`${CS_BASE_URL}/query/spice/${partNumber}`, true)
                        }
                    />
                </Box>
            )}
            {step && (
                <Box sx={iconStyle}>
                    <StepCadIcon
                        title="STEP/CAD"
                        onClick={() =>
                            handleIconClick(
                                `https://search.kemet.com/component-documentation/download/step/${partNumber}.step`
                            )
                        }
                    />
                </Box>
            )}

            <Box sx={iconStyle} onClick={handleEDAMCADClick}>
                <EmaIcon title="EDA/MCAD" />
            </Box>

            {rohs && (
                <Box sx={iconStyle}>
                    <RohsIcon
                        title="RoHS"
                        onClick={() =>
                            handleIconClick(
                                `${linkPrefix}/component-documentation/download/rohs/${partNumber}.pdf`,
                                true
                            )
                        }
                    />
                </Box>
            )}

            {/* 若有 unQualifyInfo，則顯示 InfoButton 與 Tooltip */}
            {unQualifyInfo && (
                <Tooltip
                    // 由我們自行控制顯示 / 隱藏
                    open={visible}
                    onOpenChange={handleVisibleChange}
                    // 預設觸發方式：hover (即可在 hover 時出現 tooltip)
                    trigger="hover"
                    // Tooltip 的背景和箭頭顏色
                    color="#F2F2F2"
                    // overlayInnerStyle: 設定 Tooltip 內部容器的樣式
                    overlayInnerStyle={{
                        width: '440.87px',

                        height: 'auto',
                        overflow: 'hidden',
                        // 過渡
                        transition: 'all 0.3s ease-out',
                        fontSize: '16px',
                        fontWeight: 400,
                        fontFamily: 'Poppins',
                        lineHeight: '18px',
                        background: '#F2F2F2',
                        border: '1px solid var(--Support-Colors-Text, #191A1B)',
                    }}
                    // 這裡放想要顯示的內容
                    title={
                        <div
                            style={{
                                width: '100%',
                            }}
                            // 阻止點擊冒泡，避免點第一行就關閉
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* 第一行(藍字) */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '1.5rem', // 添加自然間距
                                    background: '#F2F2F2',
                                    color: '#2E6DE5',
                                    padding: '4px 1rem',
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    '&:hover': {
                                        textDecoration: 'underline',
                                    },
                                    lineHeight: '18px',
                                }}
                            >
                                {/* 這裡放要一起顯示的 SVG icon */}
                                <InfoButton />
                                {contentTree.product.containsSpecificationChangesTooltip.content}
                            </Box>

                            {/* 第二行：只有 expanded=true 時顯示 */}
                            <div
                                style={{
                                    color: '#2E6DE5',
                                    padding: '4px 1rem',
                                    fontSize: '12px',
                                    fontWeight: 400,
                                    fontFamily: 'Poppins',
                                    lineHeight: '14px',
                                    background: '#F2F2F2',
                                    textAlign: 'left',
                                }}
                            >
                                {unQualifyInfo}
                            </div>
                        </div>
                    }
                    placement="right"
                    arrow={{ pointAtCenter: true }} // 顯示箭頭
                >
                    <Box sx={iconStyle}>
                        <InfoButton title="Info" />
                    </Box>
                </Tooltip>
            )}
        </div>
    );
};

export default IconContainer;
